/**
 * Teacher / ceremony CommandLine certificates.
 * Visual style matches learning/terminal-web/commandline-certificates.js
 * (cream parchment, gold columns, burgundy brand — name blank for write-in).
 */
import { jsPDF } from 'jspdf';

const ACCENTS = {
  'linux-101': '#8b1e2d',
  'linux-201': '#1e3a5f',
  'linux-301': '#0f5c4c',
  'linux-basics': '#8b1e2d',
  'python-basics': '#1e3a5f',
  'sql-basics': '#0f5c4c',
  'cloud-security-basics': '#6d1c2e'
};

function wrapCenteredText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text).split(/\s+/);
  let line = '';
  const lines = [];
  for (let n = 0; n < words.length; n++) {
    const test = line ? `${line} ${words[n]}` : words[n];
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = words[n];
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((l, i) => ctx.fillText(l, x, startY + i * lineHeight));
  return lines.length;
}

function drawGoldColumn(ctx, x, top, bottom, gold) {
  const mid = (top + bottom) / 2;
  const shaftW = 22;
  ctx.fillStyle = gold;
  ctx.fillRect(x - 18, top, 36, 14);
  ctx.fillRect(x - 14, top + 12, 28, 10);
  ctx.fillRect(x - shaftW / 2, top + 20, shaftW, bottom - top - 40);
  ctx.strokeStyle = 'rgba(255, 236, 179, 0.55)';
  ctx.lineWidth = 1;
  for (let i = -2; i <= 2; i++) {
    const fx = x + i * 4;
    ctx.beginPath();
    ctx.moveTo(fx, top + 24);
    ctx.lineTo(fx, bottom - 24);
    ctx.stroke();
  }
  ctx.fillStyle = gold;
  ctx.fillRect(x - 14, bottom - 22, 28, 10);
  ctx.fillRect(x - 18, bottom - 12, 36, 12);
  ctx.fillStyle = 'rgba(255, 248, 220, 0.25)';
  ctx.fillRect(x - shaftW / 2 + 2, mid - 40, 5, 80);
}

function drawSideLaurel(ctx, x, top, bottom, gold) {
  ctx.strokeStyle = gold;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(x, top);
  ctx.lineTo(x, bottom);
  ctx.stroke();
  for (let y = top + 18; y < bottom - 10; y += 22) {
    ctx.beginPath();
    ctx.ellipse(x - 7, y, 8, 3.5, -0.6, 0, Math.PI * 2);
    ctx.fillStyle = '#c9a227';
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(x + 7, y + 10, 8, 3.5, 0.6, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawLaurelWreath(ctx, cx, cy, radius, gold) {
  for (let i = 0; i < 14; i++) {
    const a1 = Math.PI * 0.55 + i * 0.12;
    const a2 = Math.PI * 0.45 - i * 0.12;
    [
      [a1, -1],
      [a2, 1]
    ].forEach(([a, side]) => {
      const px = cx + Math.cos(a) * (radius - 6);
      const py = cy + Math.sin(a) * (radius - 6);
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(a + (side < 0 ? Math.PI / 2 : -Math.PI / 2));
      ctx.beginPath();
      ctx.ellipse(0, 0, 9, 3.8, 0, 0, Math.PI * 2);
      ctx.fillStyle = i % 2 === 0 ? gold : '#d4af37';
      ctx.fill();
      ctx.restore();
    });
  }
  ctx.beginPath();
  ctx.arc(cx, cy, radius - 18, 0, Math.PI * 2);
  ctx.strokeStyle = gold;
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawCornerSeal(ctx, cx, cy, r, burgundy, gold) {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = gold;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, r - 5, 0, Math.PI * 2);
  ctx.fillStyle = '#f3e6c0';
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, r - 10, 0, Math.PI * 2);
  ctx.strokeStyle = burgundy;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.fillStyle = burgundy;
  ctx.font = `700 ${Math.round(r * 0.55)}px Georgia, Times New Roman, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('CL', cx, cy + 1);
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = burgundy;
  ctx.beginPath();
  ctx.moveTo(cx - 6, cy + r - 2);
  ctx.lineTo(cx - 14, cy + r + 22);
  ctx.lineTo(cx - 2, cy + r + 16);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx + 6, cy + r - 2);
  ctx.lineTo(cx + 14, cy + r + 22);
  ctx.lineTo(cx + 2, cy + r + 16);
  ctx.closePath();
  ctx.fill();
}

function drawCenterSeal(ctx, cx, cy, radius, burgundy, gold) {
  drawLaurelWreath(ctx, cx, cy, radius, gold);
  ctx.beginPath();
  ctx.moveTo(cx, cy - radius * 0.42);
  ctx.quadraticCurveTo(cx + radius * 0.48, cy - radius * 0.35, cx + radius * 0.44, cy + radius * 0.05);
  ctx.quadraticCurveTo(cx + radius * 0.34, cy + radius * 0.48, cx, cy + radius * 0.55);
  ctx.quadraticCurveTo(cx - radius * 0.34, cy + radius * 0.48, cx - radius * 0.44, cy + radius * 0.05);
  ctx.quadraticCurveTo(cx - radius * 0.48, cy - radius * 0.35, cx, cy - radius * 0.42);
  ctx.closePath();
  ctx.fillStyle = '#1a2438';
  ctx.fill();
  ctx.strokeStyle = gold;
  ctx.lineWidth = 2.5;
  ctx.stroke();
  ctx.fillStyle = '#fff8e7';
  ctx.font = `700 ${Math.round(radius * 0.32)}px Georgia, Times New Roman, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('CL', cx, cy - 2);
  ctx.textBaseline = 'alphabetic';
  const ry = cy + radius * 0.72;
  ctx.beginPath();
  ctx.moveTo(cx - 58, ry - 4);
  ctx.lineTo(cx - 48, ry + 16);
  ctx.lineTo(cx + 48, ry + 16);
  ctx.lineTo(cx + 58, ry - 4);
  ctx.closePath();
  ctx.fillStyle = burgundy;
  ctx.fill();
  ctx.strokeStyle = gold;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = gold;
  ctx.font = '700 11px Segoe UI, Arial, sans-serif';
  ctx.fillText('ACHIEVED', cx, ry + 10);
}

function drawHeaderSeal(ctx, cx, cy, r, gold) {
  drawLaurelWreath(ctx, cx, cy, r, gold);
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.42, 0, Math.PI * 2);
  ctx.fillStyle = '#1a2438';
  ctx.fill();
  ctx.strokeStyle = gold;
  ctx.lineWidth = 1.5;
  ctx.stroke();
  ctx.fillStyle = '#fff8e7';
  ctx.font = `700 ${Math.round(r * 0.38)}px Georgia, Times New Roman, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('CL', cx, cy + 1);
  ctx.textBaseline = 'alphabetic';
}

function drawCeoSignature(ctx, x, y, burgundy) {
  ctx.save();
  // Right-align so the block sits clear of the bottom-right seal
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = burgundy;
  ctx.font = 'italic 26px "Segoe Script", "Brush Script MT", "Lucida Handwriting", cursive';
  ctx.fillText('James Houston II', x, y);
  ctx.fillStyle = '#3a1a22';
  ctx.font = '600 11px Segoe UI, Arial, sans-serif';
  ctx.fillText('CEO Enterceptor Corporation', x, y + 18);
  ctx.restore();
}

/**
 * @param {{ title?: string, subtitle?: string, accent?: string, score?: number|string, dateStr?: string, recipientName?: string, blankName?: boolean }} opts
 */
export function drawBlankCertificateCanvas({
  title,
  subtitle,
  accent,
  score,
  dateStr,
  recipientName,
  blankName = true
} = {}) {
  const w = 1100;
  const h = 780;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const when =
    dateStr ||
    new Date().toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

  const burgundy = '#6b1c2e';
  const gold = '#c9a227';
  const cream = '#f7f0e1';
  const ink = '#3a1a22';
  const muted = '#6b4a52';
  const name = blankName ? '' : String(recipientName || '').trim();

  ctx.fillStyle = cream;
  ctx.fillRect(0, 0, w, h);
  const wash = ctx.createRadialGradient(w / 2, h / 2, 80, w / 2, h / 2, w * 0.7);
  wash.addColorStop(0, 'rgba(255,255,255,0.35)');
  wash.addColorStop(1, 'rgba(184, 134, 11, 0.08)');
  ctx.fillStyle = wash;
  ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = gold;
  ctx.lineWidth = 10;
  ctx.strokeRect(18, 18, w - 36, h - 36);
  ctx.strokeStyle = '#8b6914';
  ctx.lineWidth = 2;
  ctx.strokeRect(28, 28, w - 56, h - 56);
  ctx.strokeStyle = 'rgba(201, 162, 39, 0.65)';
  ctx.lineWidth = 1;
  ctx.strokeRect(36, 36, w - 72, h - 72);

  drawGoldColumn(ctx, 58, 100, h - 70, gold);
  drawGoldColumn(ctx, w - 58, 100, h - 70, gold);
  drawSideLaurel(ctx, 88, 120, h - 90, gold);
  drawSideLaurel(ctx, w - 88, 120, h - 90, gold);

  ctx.fillStyle = burgundy;
  ctx.fillRect(100, 48, w - 200, 56);
  ctx.strokeStyle = gold;
  ctx.lineWidth = 2;
  ctx.strokeRect(100, 48, w - 200, 56);

  ctx.fillStyle = '#fff8e7';
  ctx.font = '700 20px Georgia, Times New Roman, serif';
  ctx.textAlign = 'left';
  ctx.fillText('COMMANDLINE', 130, 84);
  ctx.textAlign = 'right';
  ctx.fillText('ENTERCEPTOR', w - 130, 84);
  drawHeaderSeal(ctx, w / 2, 76, 28, gold);

  ctx.fillStyle = burgundy;
  ctx.textAlign = 'center';
  ctx.font = '700 38px Georgia, Times New Roman, serif';
  ctx.fillText('CERTIFICATE OF ACHIEVEMENT', w / 2, 138);

  ctx.fillStyle = muted;
  ctx.font = 'italic 15px Georgia, Times New Roman, serif';
  ctx.fillText('This certifies successful completion of the requirements for', w / 2, 168);

  // Seal first — course title sits below it so it is never covered
  drawCenterSeal(ctx, w / 2, 268, 62, burgundy, gold);

  const courseTitle = String(title || 'CommandLine Achievement').trim();
  const coursePhase = String(subtitle || 'Course completion').trim().toUpperCase();
  ctx.fillStyle = burgundy;
  ctx.font = '700 30px Georgia, Times New Roman, serif';
  const titleLines = wrapCenteredText(ctx, courseTitle, w / 2, 370, w - 280, 34);
  const phaseY = 370 + titleLines * 18 + 16;
  ctx.fillStyle = burgundy;
  ctx.font = '700 15px Segoe UI, Arial, sans-serif';
  ctx.fillText(coursePhase, w / 2, phaseY);
  const phaseWidth = Math.min(280, Math.max(160, ctx.measureText(coursePhase).width + 24));
  ctx.strokeStyle = gold;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(w / 2 - phaseWidth / 2, phaseY + 8);
  ctx.lineTo(w / 2 + phaseWidth / 2, phaseY + 8);
  ctx.stroke();

  const ruleY = phaseY + 36;
  ctx.strokeStyle = gold;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(w / 2 - 200, ruleY);
  ctx.lineTo(w / 2 - 12, ruleY);
  ctx.moveTo(w / 2 + 12, ruleY);
  ctx.lineTo(w / 2 + 200, ruleY);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(w / 2, ruleY - 6);
  ctx.lineTo(w / 2 + 6, ruleY);
  ctx.lineTo(w / 2, ruleY + 6);
  ctx.lineTo(w / 2 - 6, ruleY);
  ctx.closePath();
  ctx.fillStyle = gold;
  ctx.fill();

  const awardLabelY = ruleY + 32;
  ctx.fillStyle = ink;
  ctx.font = '600 13px Georgia, Times New Roman, serif';
  ctx.fillText('AWARDED TO', w / 2, awardLabelY);

  const nameLineY = awardLabelY + 42;
  if (name) {
    ctx.fillStyle = ink;
    ctx.font = '700 30px Georgia, Times New Roman, serif';
    wrapCenteredText(ctx, name, w / 2, nameLineY - 8, w - 300, 34);
    ctx.strokeStyle = gold;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(w / 2 - 220, nameLineY + 14);
    ctx.lineTo(w / 2 + 220, nameLineY + 14);
    ctx.stroke();
  } else {
    ctx.strokeStyle = ink;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(w / 2 - 240, nameLineY);
    ctx.lineTo(w / 2 + 240, nameLineY);
    ctx.stroke();
    ctx.fillStyle = muted;
    ctx.font = 'italic 12px Segoe UI, Arial, sans-serif';
    ctx.fillText('(Write student name — or have your teacher fill this in)', w / 2, nameLineY + 22);
  }

  let meta = `Date: ${when}`;
  if (score != null && score !== '' && Number.isFinite(Number(score))) {
    meta += `     ·     Score: ${Math.round(Number(score))}%`;
  }
  ctx.fillStyle = ink;
  ctx.font = '600 15px Georgia, Times New Roman, serif';
  ctx.fillText(meta, w / 2, Math.min(h - 110, nameLineY + 52));

  drawCornerSeal(ctx, 130, h - 95, 36, burgundy, gold);
  drawCeoSignature(ctx, w - 185, h - 118, burgundy);
  drawCornerSeal(ctx, w - 130, h - 95, 36, burgundy, gold);

  ctx.fillStyle = muted;
  ctx.font = '11px Segoe UI, Arial, sans-serif';
  ctx.fillText(
    'CommandLine training achievement — not a CompTIA, vendor, or industry certification.',
    w / 2,
    h - 48
  );
  ctx.fillStyle = burgundy;
  ctx.font = '700 13px Segoe UI, Arial, sans-serif';
  ctx.fillText('cmdlineacademy.com', w / 2, h - 28);

  // accent kept for API compat / future per-track tint
  void accent;

  return canvas;
}

export function downloadBlankCertificatePdf(cert) {
  const accent = cert?.accent || ACCENTS[cert?.id] || '#8b1e2d';
  const canvas = drawBlankCertificateCanvas({
    title: cert?.title,
    subtitle: cert?.subtitle,
    accent,
    score: cert?.score,
    recipientName: cert?.recipientName,
    blankName: cert?.blankName !== false && !cert?.recipientName
  });
  const pdf = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'letter'
  });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageW, pageH);
  const safe = String(cert?.title || 'certificate')
    .replace(/[^\w\s-]+/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 60);
  pdf.save(`CommandLine-${safe || 'certificate'}-Certification.pdf`);
}
